// src/hooks/useAutoRefreshJWT.js
import { useEffect, useRef, useCallback } from 'react';
import { refreshJWT } from '../api/endpoints';

/**
 * Hook para refrescar automáticamente el token JWT
 *
 * Comportamiento:
 * - Refresca el token cada 45 minutos si hay actividad del usuario
 * - Hace logout después de 1 hora de inactividad
 * - Detecta actividad: mouse, teclado, scroll, touch
 *
 * @param {Function} onLogout - Callback para ejecutar cuando expire por inactividad
 */
export function useAutoRefreshJWT(onLogout) {
    const lastActivityRef = useRef(Date.now());
    const refreshIntervalRef = useRef(null);
    const inactivityCheckRef = useRef(null);

    // Constantes
    const REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutos
    const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hora
    const INACTIVITY_CHECK_INTERVAL = 60 * 1000; // Revisar cada 1 minuto

    // Actualizar última actividad
    const updateActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);

    // Función para refrescar el token
    const performRefresh = useCallback(async () => {
        const timeSinceActivity = Date.now() - lastActivityRef.current;

        // Si ha pasado más de 1 hora de inactividad, hacer logout
        if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
            console.log('[AUTO-REFRESH] 1 hora de inactividad detectada - haciendo logout');
            clearInterval(refreshIntervalRef.current);
            clearInterval(inactivityCheckRef.current);
            if (onLogout) {
                onLogout();
            }
            return;
        }

        // Si hay actividad reciente, refrescar token
        console.log('[AUTO-REFRESH] Refrescando token JWT...');
        const result = await refreshJWT();

        if (result.ok) {
            console.log('[AUTO-REFRESH] Token refrescado exitosamente');
        } else {
            console.error('[AUTO-REFRESH] Error al refrescar token:', result.mensaje);
            // Si falla el refresh, probablemente el token expiró
            clearInterval(refreshIntervalRef.current);
            clearInterval(inactivityCheckRef.current);
            if (onLogout) {
                onLogout();
            }
        }
    }, [onLogout, INACTIVITY_TIMEOUT]);

    // Verificar inactividad periódicamente
    const checkInactivity = useCallback(() => {
        const timeSinceActivity = Date.now() - lastActivityRef.current;

        if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
            console.log('[AUTO-REFRESH] Usuario inactivo por 1 hora - cerrando sesión');
            clearInterval(refreshIntervalRef.current);
            clearInterval(inactivityCheckRef.current);
            if (onLogout) {
                onLogout();
            }
        }
    }, [onLogout, INACTIVITY_TIMEOUT]);

    useEffect(() => {
        // Eventos que indican actividad del usuario
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
        ];

        // Agregar listeners de actividad
        events.forEach(event => {
            document.addEventListener(event, updateActivity, { passive: true });
        });

        // Iniciar interval de refresh automático cada 45 minutos
        refreshIntervalRef.current = setInterval(performRefresh, REFRESH_INTERVAL);

        // Iniciar verificación de inactividad cada 1 minuto
        inactivityCheckRef.current = setInterval(checkInactivity, INACTIVITY_CHECK_INTERVAL);

        console.log('[AUTO-REFRESH] Sistema de refresh automático inicializado');
        console.log('[AUTO-REFRESH] - Token se refresca cada 45 minutos si hay actividad');
        console.log('[AUTO-REFRESH] - Logout automático después de 1 hora de inactividad');

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, updateActivity);
            });

            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }

            if (inactivityCheckRef.current) {
                clearInterval(inactivityCheckRef.current);
            }

            console.log('[AUTO-REFRESH] Sistema de refresh automático detenido');
        };
    }, [updateActivity, performRefresh, checkInactivity, REFRESH_INTERVAL, INACTIVITY_CHECK_INTERVAL]);

    // Retornar función para refrescar manualmente si es necesario
    return { refreshNow: performRefresh };
}
